import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router";
import { useFriends } from "@/context/friends-context";
import { useFriendMode } from "@/context/friend-mode-context";
import { ComprasToast, useComprasToast } from "@/components/ui/compras-toast";
import { onLostFriendEdit } from "@/lib/pending-saves";
import { PersonSwitcher } from "@/components/person-switcher";
import { useLogoutWithConfirm } from "@/components/logout-confirm";
import { parseWeekPath, weekPath } from "@/lib/week-paths";
import { addWeeks, formatWeekRange, getCurrentWeekId, getTodayDayOfWeek } from "@/lib/week-utils";
import {
  MenuIcon,
  UsersIcon,
  CalendarDaysIcon,
  CircleDotIcon,
  ListIcon,
  LogOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SettingsIcon,
} from "lucide-react";

const WEEK_ID_RE = /^\d{4}-\d{2}-\d{2}$/;

export function AppNavbar() {
  const location = useLocation();
  // Every navbar destination goes through the friend-mode guard: leaving a
  // friend's week with an unsaved change asks first.
  const { leaveTo } = useFriendMode();
  const { requestLogout, logoutDialog } = useLogoutWithConfirm();
  const { toast, show: showToast, dismiss: dismissToast } = useComprasToast();
  const { friends } = useFriends();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  // Publish the navbar's measured height as a CSS variable so descendants
  // (e.g. the Compras toast) can position themselves below it without hard-
  // coding a magic number that breaks the moment a sub-bar is added.
  useLayoutEffect(() => {
    const node = navbarRef.current;
    if (!node) return;
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--app-navbar-height",
        `${node.offsetHeight}px`,
      );
    };
    setVar();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(setVar);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Leaving friend mode another way (the back button) saves on the way out;
  // if that save fails, say so here, wherever the planner ended up.
  useEffect(
    () =>
      onLostFriendEdit((username) =>
        showToast({ message: `No se guardó tu último cambio para ${username}.`, tone: "error" }),
      ),
    [showToast],
  );

  // Every accepted friend can plan for the other: the switcher shows with the first one.
  const hasPlanningFriends = friends.some((f) => f.status === "accepted");

  const pendingCount = friends.filter(
    (f) => f.status === "pending" && f.direction === "received",
  ).length;

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  // Determine context. The navbar sits above the routes (and above a friend's
  // week provider), so the week and the person come from the URL.
  const weekRoute = parseWeekPath(location.pathname);
  const isWeekView = weekRoute !== null;
  const friendId = weekRoute?.friendId ?? null;
  const isCalendar = location.pathname === "/calendar";
  const isRegimen = location.pathname === "/regimen";
  const isFriends = location.pathname === "/friends";
  const isPreferences = location.pathname === "/preferencias";

  // Prev/next week keep the person and the sub-page.
  function goToAdjacentWeek(delta: number) {
    if (!weekRoute) return;
    leaveTo(weekPath(weekRoute.friendId, addWeeks(weekRoute.weekId, delta), weekRoute.sub));
  }

  // Title
  let title = "Plan Nutricional";
  if (weekRoute && WEEK_ID_RE.test(weekRoute.weekId)) {
    title = formatWeekRange(weekRoute.weekId);
  } else if (isCalendar) {
    title = "Calendario";
  } else if (isRegimen) {
    title = "Regímenes por día";
  } else if (isFriends) {
    title = "Amigos";
  } else if (isPreferences) {
    title = "Preferencias";
  }

  // "Hoy" stays with the person being planned for (decision §8.2).
  function goToToday() {
    setMenuOpen(false);
    const weekId = getCurrentWeekId();
    const today = getTodayDayOfWeek(weekId) ?? "mon";
    leaveTo(weekPath(friendId, weekId, today));
  }

  function menuNavigate(path: string) {
    setMenuOpen(false);
    leaveTo(path);
  }

  return (
    <div ref={navbarRef} className="border-b bg-background">
      {logoutDialog}
      <ComprasToast toast={toast} onDismiss={dismissToast} />
      <div className="flex items-center justify-between px-4 py-3 mx-auto max-w-3xl">
        {/* Title (with prev/next week arrows in week view) */}
        <div className="flex items-center min-w-0 mr-3">
          {hasPlanningFriends && <PersonSwitcher />}
          {isWeekView && (
            <button
              onClick={() => goToAdjacentWeek(-1)}
              aria-label="Semana anterior"
              className={`flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0 ${hasPlanningFriends ? "" : "-ml-2"}`}
            >
              <ChevronLeftIcon className="size-5" />
            </button>
          )}
          <h1 className="text-base font-bold tracking-tight truncate">
            {title}
          </h1>
          {isWeekView && (
            <button
              onClick={() => goToAdjacentWeek(1)}
              aria-label="Semana siguiente"
              className="flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
            >
              <ChevronRightIcon className="size-5" />
            </button>
          )}
        </div>

        {/* Right icons: today, calendar, hamburger */}
        <div className="flex items-center gap-1">
          <button
            onClick={goToToday}
            aria-label="Hoy"
            className="flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <CircleDotIcon className="size-5" />
          </button>

          <Link
            to="/calendar"
            aria-label="Calendario"
            title="Calendario"
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              leaveTo("/calendar");
            }}
            className={`flex items-center justify-center size-8 rounded-md transition-colors ${
              isCalendar
                ? "text-foreground bg-muted"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            }`}
          >
            <CalendarDaysIcon className="size-5" />
          </Link>

          {/* Hamburger menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={
                pendingCount > 0
                  ? `Menú, ${pendingCount === 1 ? "1 solicitud de amistad" : `${pendingCount} solicitudes de amistad`}`
                  : "Menú"
              }
              aria-expanded={menuOpen}
              title="Menú"
              className="relative flex items-center justify-center size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <MenuIcon className="size-5" />
              {pendingCount > 0 && (
                <span className="absolute top-0.5 right-0.5 size-2 rounded-full bg-red-500" />
              )}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 rounded-lg border bg-background shadow-lg z-50 py-1">
                <button
                  onClick={goToToday}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors w-full text-left"
                >
                  <CircleDotIcon className="size-4 text-muted-foreground" />
                  Hoy
                </button>
                <button
                  onClick={() => menuNavigate("/calendar")}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors w-full text-left"
                >
                  <CalendarDaysIcon className="size-4 text-muted-foreground" />
                  Calendario
                </button>
                <button
                  onClick={() => menuNavigate("/friends")}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors w-full text-left"
                >
                  <UsersIcon className="size-4 text-muted-foreground" />
                  Amigos
                  {pendingCount > 0 && (
                    <span className="ml-auto text-xs font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">
                      {pendingCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => menuNavigate("/regimen")}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors w-full text-left"
                >
                  <ListIcon className="size-4 text-muted-foreground" />
                  Regímenes por día
                </button>
                <button
                  onClick={() => menuNavigate("/preferencias")}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors w-full text-left"
                >
                  <SettingsIcon className="size-4 text-muted-foreground" />
                  Preferencias
                </button>
                <div className="border-t my-1" />
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    requestLogout();
                  }}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted transition-colors w-full text-left text-red-600"
                >
                  <LogOutIcon className="size-4" />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
